// @flow

import _ from 'lodash';

import { ValidatableMixin } from 'validatable';
import { Person as PersonBase } from 'person-model';
import ko from 'rgs-knockout';
import vb from 'validation-builder';
import { serverTime } from 'observable-server-time';
import kv from 'rgs-knockout-validation';
import ObservableMap from 'observable-map';
import stringify from 'json-stable-stringify';

import { RestIdentityDocument } from "calc-common";
import type { IRestIdentityDocument } from "calc-common";
import type { Observable } from 'rgs-knockout';

// myrgs-user-person-model
import type { MyRgsUserPersonOptions } from './myrgs-user-person-model.flow';

type Options = MyRgsUserPersonOptions;

export class MyRgsUserPerson extends ValidatableMixin(PersonBase) {
	IdentityDocument: IRestIdentityDocument;
	DmsEmailForMailing: Observable<string>;

	constructor (options: MyRgsUserPersonOptions, ...rest) {
		const optionsDefault: Options = {
			dictionaries: {
				kladr: {
					streets: new ObservableMap({ hash: (v) => stringify(v) }),
					settlements: new ObservableMap({ hash: (v) => stringify(v) }),
					settlementsRsa: new ObservableMap(),
				},
				countries: new ObservableMap(),
				documentTypes: {},
			},
		};
		options = _.defaultsDeep({}, options, optionsDefault);

		super(options, ...rest);

		/* Проверка на консистентность ввода ФИО */
		const isPersonFullNameEntered = ko.pureComputed(() => {
			return Boolean(this.FirstName()) && Boolean(this.LastName());
		});

		this.FirstName.extend({
			validation: vb.all([
				vb.required(),
				vb.nameCyrillicOrLatinWithSymbols(),
				vb.lengthMin(1),
				vb.lengthMax(30),
				vb.if(
					() => isPersonFullNameEntered(),
					vb.sameCharsetFullName(() => this.FullName()),
				),
			]),
		});

		this.LastName.extend({
			validation: vb.all([
				vb.required(),
				vb.nameCyrillicOrLatinWithSymbols(),
				vb.lengthMin(1),
				vb.lengthMax(30),
				vb.if(
					() => isPersonFullNameEntered(),
					vb.sameCharsetFullName(() => this.FullName()),
				),
			]),
		});

		this.MiddleName.extend({
			validation: vb.all([
				vb.nameCyrillicOrLatinWithSymbols(),
				vb.lengthMin(1),
				vb.lengthMax(30),
				vb.if(
					() => isPersonFullNameEntered(),
					vb.sameCharsetFullName(() => this.FullName()),
				),
			]),
		});

		this.BirthDay.extend({
			validation: vb.all([
				vb.required(),
				vb.momentIsValid(),
				vb.momentMinAge({
					age: 18,
					from: serverTime,
				}),
				vb.momentMaxAge({
					age: 130,
					from: serverTime,
				}),
			]),
		});

		this.IsStablePersonalDataEditingEnabled = ko.observable(false)
			.extend({ toFromJS: true });

		this.Phone.extend({
			validation: vb.all([
				vb.required(),
				vb.phone11Digits(),
			]),
		});

		this.IsPhoneConfirmed = ko.observable().extend({ toFromJS: true });

		this.IdentityDocument = new RestIdentityDocument(options);

		this.IdentityDocument.singleSeriesNumberInput
			.extend({ notify: 'always' })
			.extend({
				validation: vb.all([
					vb.required(),
					vb.physicalPersonLkIdentityDocumentValue(this.IdentityDocument.Type),
				]),
			});

		this.IdentityDocument.Type
			.extend({ notify: 'always' })
			.extend({
				validation: vb.all([
					vb.required(),
				]),
			});

		this.IdentityDocument.Series
			.extend({ notify: 'always' });

		this.IdentityDocument.Number
			.extend({ notify: 'always' });

		this.Registration.SettlementNameRsa.extend({
			required: true,
			validation: [{
				validator: (v) => options.dictionaries.kladr.settlementsRsa.has(v),
				message: 'выберите из меню',
			}],
		});

		this.IdentityDocument.IssueCountryID.extend({
			validation: vb.all([
				vb.if(
					() => this.IdentityDocument.Type.isDocumentForeignPassport(),
					vb.required(),
				),
			]),
		});

		this.DmsEmailForMailing = ko.observable()
			.extend({ toFromJS: true })
			.extend({
				validation: vb.all([
					vb.email(),
				]),
			});
	}

	validationArray () {
		return [
			this.Registration.SettlementNameRsa,
		]
			.concat(_.filter(this, kv.utils.isValidatable));
	}
}
